/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsShare } from "../funcs/skillsShare.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SkillShareLink } from "../models/components/skillsharelink.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ShareSkillRequest,
  ShareSkillSecurity,
} from "../models/operations/shareskill.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ShareSkillMutationVariables = {
  request: ShareSkillRequest;
  security?: ShareSkillSecurity | undefined;
  options?: RequestOptions;
};

export type ShareSkillMutationData = SkillShareLink;

export type ShareSkillMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * share skills
 *
 * @remarks
 * Create a public share link for a skill. Repeated requests return the existing active link, so each skill has at most one active share token.
 */
export function useShareSkillMutation(
  options?: MutationHookOptions<
    ShareSkillMutationData,
    ShareSkillMutationError,
    ShareSkillMutationVariables
  >,
): UseMutationResult<
  ShareSkillMutationData,
  ShareSkillMutationError,
  ShareSkillMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildShareSkillMutation(client, options),
    ...options,
  });
}

export function mutationKeyShareSkill(): MutationKey {
  return ["@gram/client", "skills", "share"];
}

export function buildShareSkillMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ShareSkillMutationVariables,
  ) => Promise<ShareSkillMutationData>;
} {
  return {
    mutationKey: mutationKeyShareSkill(),
    mutationFn: function shareSkillMutationFn({
      request,
      security,
      options,
    }): Promise<ShareSkillMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsShare(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
